/* eslint-disable no-console */
/**
 * Solomon Panel Component
 *
 * The main floating AI assistant panel that appears at the bottom of the screen.
 * Orchestrates the collapsed trigger, expanded panel container, and preview flow.
 * Shows a welcome prompt on first open; user initiates conversation by typing.
 */

import { useState, useCallback } from 'react';

import { useAuth } from '@pray/shared/hooks/useAuth';

import { useMessageStore } from 'hooks/solomon/useMessageStore';
import { useSolomonAgent } from 'hooks/solomon/useSolomonAgent';
import type { SolomonStreamEvent } from 'hooks/solomon/useSolomonAgent';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import { Permissions } from 'utils/auth';

import {
  PanelContainer,
  PanelTrigger,
  PanelHeader,
  PromptInput,
  PreviewPane,
  ThinkingAnimation,
  ConversationView,
  DropZone,
  SlotSelector,
} from './components';
import { useSolomonPanelContext } from './context';
import { useThinkingMessage } from './hooks';
import type { SolomonPanelProps } from './types';
import { getImageDimensions, detectBestSlot } from './utils/imageUtils';

import styles from './SolomonPanel.module.scss';

export function SolomonPanel({ className }: SolomonPanelProps) {
  const { permissions } = useAuth();
  const isAdmin = permissions.has(Permissions.STUDIO_ADMIN);
  const isSolomonEnabled = useFeatureFlag('solomon-agent');

  const {
    panelState,
    panelPosition,
    setPanelState,
    setPanelPosition,
    togglePanel,
    isLoading,
    executeAction,
    acceptAction,
    rejectAction,
    activePreview,
    pageContext,
    artistId,
    imageGenFlow,
    notifyImageApplied,
    slotSelection,
    startSlotSelection,
    completeSlotSelection,
    cancelSlotSelection,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
    pendingApprovals,
    addPendingApproval,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
    approveAction,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
    rejectPendingAction,
  } = useSolomonPanelContext();

  // Track accepting state - MUST be before any early returns (Rules of Hooks)
  const [isAccepting, setIsAccepting] = useState(false);

  // Single source of truth for all messages (unified messaging)
  const messageStore = useMessageStore();

  // Track dropped image for the PromptInput (passed down from DropZone)
  const [, setDroppedImageFromPanel] = useState<File | null>(null);

  // Handle image drop from the DropZone wrapper
  const handleImageDrop = useCallback(
    async (file: File) => {
      console.log('[SolomonPanel] Image dropped via DropZone:', file.name);

      // Check if we have image context from imageGenFlow (user clicked "Generate with Solomon" on a card)
      if (imageGenFlow?.context?.imageKey) {
        // We have context - use the existing flow
        setDroppedImageFromPanel(file);
      } else {
        // No context - detect dimensions and start slot selection
        try {
          const dimensions = await getImageDimensions(file);
          const suggestion = detectBestSlot(dimensions.ratio);
          startSlotSelection(file, dimensions, suggestion);
        } catch (err) {
          console.error('[SolomonPanel] Failed to get image dimensions:', err);
          // Fall back to just setting the dropped image
          setDroppedImageFromPanel(file);
        }
      }
    },
    [imageGenFlow, startSlotSelection]
  );

  // Handle SSE stream events - detect when update_profile_image tool succeeds
  // This bridges the direct chat flow to the ImageCard callback system
  const handleAgentEvent = useCallback(
    (event: SolomonStreamEvent) => {
      // Only handle successful tool_result events for update_profile_image
      if (event.type === 'tool_result' && event.tool === 'update_profile_image' && !event.isError) {
        console.log('[SolomonPanel] Detected update_profile_image tool result:', event);

        // Extract data from tool result
        const resultData = (event.result as Record<string, unknown>)?.data || event.result;
        const imageUrl = (resultData as Record<string, unknown>)?.imageUrl as string | undefined;
        const previousUrl = (resultData as Record<string, unknown>)?.previousUrl as string | null;

        // Get imageKey from the imageGenFlow context (set when user clicks "Generate with Solomon")
        const imageKey = imageGenFlow?.context?.imageKey as string | undefined;

        console.log('[SolomonPanel] Extracted image data:', { imageKey, imageUrl, previousUrl });

        if (imageKey && imageUrl) {
          // Notify ProfileImagesSection that the image was applied
          notifyImageApplied(imageKey, imageUrl, previousUrl || null);
        } else {
          console.warn('[SolomonPanel] Missing imageKey or imageUrl in tool result', {
            imageKey,
            imageUrl,
            hasImageGenFlow: !!imageGenFlow,
            context: imageGenFlow?.context,
          });
        }
      }
    },
    [imageGenFlow, notifyImageApplied]
  );

  // Initialize agent hook for chat mode (uses messageStore for unified messaging)
  const agent = useSolomonAgent({
    artistId: artistId || '',
    context: pageContext || 'default',
    messageStore, // Pass messageStore for single source of truth
    onEvent: handleAgentEvent,
    onApprovalRequired: addPendingApproval,
  });

  const isExpanded = panelState === 'expanded';

  const hasNotifications = messageStore.hasMessages;

  // Thinking state - crown rotates and title changes
  const isSolomonThinking = isLoading || agent.isThinking || agent.isStreaming;
  const thinkingMessage = useThinkingMessage(isSolomonThinking);

  const handlePromptSubmit = useCallback(
    async (prompt: string) => {
      console.log('[SolomonPanel] handlePromptSubmit called', {
        promptLength: prompt.length,
        isStreaming: agent.isStreaming,
      });
      // Send message via agent
      await agent.sendMessage(prompt);
      console.log('[SolomonPanel] handlePromptSubmit completed');
    },
    [agent]
  );

  // Handle prompt submission with an attached image
  const handlePromptWithImage = useCallback(
    async (prompt: string, image: File) => {
      console.log('[SolomonPanel] handlePromptWithImage called', {
        promptLength: prompt.length,
        imageSize: image.size,
        imageType: image.type,
      });
      // Send message with image via agent
      await agent.sendMessageWithImage(prompt, image);
      console.log('[SolomonPanel] handlePromptWithImage completed');
    },
    [agent]
  );

  const handleAcceptPreview = useCallback(async () => {
    console.log('[SolomonPanel] handleAcceptPreview called, activePreview:', !!activePreview);
    if (!activePreview) {
      console.error('[SolomonPanel] handleAcceptPreview: No activePreview!');
      return;
    }

    console.log('[SolomonPanel] Calling acceptAction with recommendation:', activePreview.recommendation.id);
    setIsAccepting(true);
    try {
      await acceptAction(activePreview.recommendation);
      console.log('[SolomonPanel] acceptAction completed');
    } catch (error) {
      console.error('[SolomonPanel] acceptAction failed:', error);
    } finally {
      setIsAccepting(false);
    }
  }, [activePreview, acceptAction]);

  const handleRejectPreview = useCallback(() => {
    if (!activePreview) return;
    rejectAction(activePreview.recommendation);
  }, [activePreview, rejectAction]);

  const handleDownloadPreview = useCallback(() => {
    if (!activePreview) return;

    // Create a temporary link to download the image
    const link = document.createElement('a');
    link.href = activePreview.result.imageUrl;
    link.download = `solomon-${activePreview.recommendation.actionType}-${Date.now()}.png`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [activePreview]);

  const handleRetryPreview = useCallback(async () => {
    if (!activePreview) return;
    // Re-execute the same recommendation
    await executeAction(activePreview.recommendation);
  }, [activePreview, executeAction]);

  const handleMinimize = useCallback(() => {
    setPanelState('collapsed');
  }, [setPanelState]);

  const handleClose = useCallback(() => {
    setPanelState('collapsed');
  }, [setPanelState]);

  // Hide Solomon Panel for non-admin users or when feature flag is disabled
  if (!isAdmin || !isSolomonEnabled) {
    return null;
  }

  return (
    <>
      {/* Collapsed trigger pill */}
      <PanelTrigger
        isExpanded={isExpanded}
        hasNotifications={hasNotifications}
        onClick={togglePanel}
        isThinking={isSolomonThinking}
      />

      {/* Expanded panel */}
      <PanelContainer isExpanded={isExpanded} position={panelPosition} onClose={handleClose} className={className}>
        <DropZone onImageDrop={handleImageDrop} disabled={!!activePreview || agent.isStreaming}>
          <div className={styles.panelContent}>
            {/* Header - static title bar with position toggle */}
            <PanelHeader
              title="Solomon"
              onMinimize={handleMinimize}
              onClose={handleClose}
              position={panelPosition}
              onPositionChange={setPanelPosition}
            />

            {/* Scrollable content area - unified view */}
            <div className={styles.scrollArea}>
              {/* Slot selection - shown when user drops image without context */}
              {slotSelection.isActive && (
                <div className={styles.slotSelectionContainer}>
                  <p className={styles.slotSelectionPrompt}>
                    I received your image! Which profile slot would you like to use it for?
                  </p>
                  <SlotSelector
                    onSelect={completeSlotSelection}
                    suggestion={slotSelection.suggestion}
                    disabled={agent.isStreaming}
                  />
                  <button type="button" className={styles.cancelButton} onClick={cancelSlotSelection}>
                    Cancel
                  </button>
                </div>
              )}

              {/* Active preview - shown when an action produces a result */}
              {activePreview && !slotSelection.isActive && (
                <PreviewPane
                  result={activePreview.result}
                  recommendation={activePreview.recommendation}
                  onAccept={handleAcceptPreview}
                  onReject={handleRejectPreview}
                  onDownload={handleDownloadPreview}
                  onRetry={handleRetryPreview}
                  isAccepting={isAccepting}
                />
              )}

              {/* Unified conversation view - always show (empty state handles welcome prompt) */}
              {!activePreview && !slotSelection.isActive && (
                <ConversationView
                  messages={agent.messages}
                  currentResponse={agent.currentResponse}
                  currentThinking={agent.currentThinking}
                  isStreaming={agent.isStreaming}
                  isThinking={agent.isThinking}
                  activeToolUse={agent.activeToolUse}
                  onRegenerate={handlePromptSubmit}
                />
              )}
            </div>

            {/* Animation zone - Claude Code-inspired UX: appears above input when thinking */}
            <ThinkingAnimation isActive={isSolomonThinking} message={thinkingMessage} />

            {/* Prompt input at bottom */}
            {/* Input stays enabled during streaming - shows stop button and allows queuing messages */}
            <PromptInput
              onSubmit={handlePromptSubmit}
              onSubmitWithImage={handlePromptWithImage}
              isStreaming={agent.isStreaming}
              onStop={agent.stopStream}
              placeholder="Ask Solomon anything..."
              defaultValue={imageGenFlow?.prompt ?? ''}
            />
          </div>
        </DropZone>
      </PanelContainer>
    </>
  );
}

export default SolomonPanel;
